import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material'
import { useRef, useState } from 'react'
import { useReactToPrint } from 'react-to-print'
import CircularProgress from '@mui/material/CircularProgress'
import { COLORS } from '~theme/colors'
import { PrinterIcon } from '~components/icons'
import { useGetPortInfoById } from '~hooks/queries/port-info/use-get-port-info'
import { ExactScopeName, useHasScope } from '~hooks/permissions/use-has-scope'
import { useDateFormat } from '~hooks/use-date-format'
import { formatDate } from '~utils/dates'
import { EditEntryModal } from './edit-entry/edit-entry-modal'
import type { PortInfoFile } from '../types'
import { sectionBoxSx, fieldsContainerSx } from '../styles'
import { InfoBlock, RemarkBlock } from './shared'

function normalizeFiles(
  files: PortInfoFile[] | undefined,
): { id: string; name: string; fileSize: string }[] {
  if (!files) return []
  return files.map((f) => ({
    id: f._id,
    name: decodeURIComponent(f.title),
    fileSize: f.fileSize,
  }))
}

interface EntryDetailProps {
  entryId: string
}

export function EntryDetail({ entryId }: EntryDetailProps) {
  const { data: entry, isLoading } = useGetPortInfoById(entryId)
  const dateFormat = useDateFormat('literal')
  const updateDateFormat = useDateFormat('with hours and minutes')
  const [showEditModal, setShowEditModal] = useState(false)
  const canWritePortInfo = useHasScope(
    ExactScopeName.technicalManagement,
    ExactScopeName.portInfoWrite,
  )
  // biome-ignore lint/suspicious/noExplicitAny: any is needed for ref typing in react-to-print
  const printRef = useRef<HTMLDivElement>(null as any)
  const handlePrint = useReactToPrint({ contentRef: printRef })

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress aria-label="Loading…" />
      </Box>
    )
  }

  if (!entry) {
    return (
      <Typography variant="paragraph2" color={COLORS.greys.dark}>
        Unable to load entry details
      </Typography>
    )
  }

  const isContinent = entry.type === 'continent'
  const locationLabel = isContinent ? 'Continent' : 'Country'
  const locationValue = isContinent ? entry.continentName : entry.countryName

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
        <Tooltip title="Print Entry">
          <IconButton size="small" onClick={() => handlePrint()}>
            <PrinterIcon width="20px" height="20px" />
          </IconButton>
        </Tooltip>
        {canWritePortInfo && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowEditModal(true)}
          >
            Edit Entry
          </Button>
        )}
      </Box>
      <Box ref={printRef}>
        <Box sx={sectionBoxSx}>
          <Typography variant="paragraphBold2">Entry Information</Typography>
          <Box sx={fieldsContainerSx}>
            <InfoBlock label={locationLabel} value={locationValue} />
            <InfoBlock label="Subject" value={entry.subject} />
            <InfoBlock
              label="Date of Information"
              value={
                entry.dateOfInformation
                  ? formatDate(entry.dateOfInformation, dateFormat)
                  : '-'
              }
            />
            <InfoBlock
              label="Vessel"
              value={
                entry.vesselName
                  ? `${entry.vesselName} (${entry.imoNumber})`
                  : '-'
              }
            />
            <InfoBlock label="Pool/Segment" value={entry.pool || '-'} />
          </Box>
        </Box>

        <Box sx={sectionBoxSx}>
          <Typography variant="paragraphBold2">Remarks</Typography>
          <Box sx={{ mt: 2 }}>
            <RemarkBlock
              value={entry.remarks}
              attachedFiles={normalizeFiles(entry.files)}
              emptyText="No remarks provided"
            />
          </Box>
        </Box>

        <Typography
          variant="caption"
          sx={{ color: COLORS.greys.dark }}
          component="p"
        >
          Last updated by {entry?.updatedBy} on{' '}
          {formatDate(entry?.updatedAt || '', updateDateFormat)}
        </Typography>
      </Box>

      {showEditModal && (
        <EditEntryModal
          entryId={entryId}
          onClose={() => setShowEditModal(false)}
        />
      )}
    </Box>
  )
}
